import { QueryKeys } from "@/constants";
import { emitEvent, EventConstants } from "@/lib/event-emitter";
import type FeatureImage from "@/model/feature-image";
import { ErrorNotificationType } from "@/pages/components/error-notification";
import {
  downloadImageFromUrl,
  featureImagesList,
} from "@/services/feature-images";
import type { UseQueryResult } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";

export const useCachedImage = (url: string) => {
  return useQuery({
    queryKey: [QueryKeys.FEATURE_IMAGE, url],
    queryFn: () => downloadImageFromUrl(url),
    enabled: true,
    retry: false,
    staleTime: 1000 * 60 * 60 * 24, // Keep cached for 24 hours
    gcTime: 1000 * 60 * 60 * 24, // Persist in memory for 24 hours
  });
};

export const useQueryFeatureImages = (): UseQueryResult<
  FeatureImage[],
  Error
> => {
  const queryResult = useQuery<FeatureImage[], Error>({
    queryKey: [QueryKeys.FEATURE_IMAGES, window.location.hostname],
    queryFn: () => featureImagesList(),
    enabled: true,
    retry: false,
    staleTime: 1000 * 60 * 60 * 24, // Keep cached for 24 hours
    gcTime: 1000 * 60 * 60 * 24, // Persist in memory for 24 hours
  });

  if (queryResult.isError && queryResult.error) {
    emitEvent(EventConstants.ADD_ERROR, {
      message: "Failed to fetch feature images",
      type: ErrorNotificationType.ERROR,
    });
  }

  return queryResult;
};
